import * as THREE from 'three';
import { CLOCK_START_MINUTES } from '../../../shared/game/rest-types.js';
import { createSky, sampleSky } from './day-cycle.js';
import { TUNING } from '../graphics-tuning.js';

const MIN_REACH = 30, MAX_REACH = 200, look = new THREE.Vector3();

/**
 * A faint sky for surfaces to reflect: zenith, horizon haze and ground bounce in an equirectangular strip, prefiltered for
 * the physically based materials. It is rebuilt only when the sky's colours have drifted, a few times an in-game hour at most.
 */
function createSkyReflection(renderer: THREE.WebGLRenderer) {
  const width = 8, height = 32, data = new Uint8Array(width * height * 4);
  const strip = new THREE.DataTexture(data, width, height, THREE.RGBAFormat); strip.mapping = THREE.EquirectangularReflectionMapping;
  strip.colorSpace = THREE.SRGBColorSpace; strip.magFilter = strip.minFilter = THREE.LinearFilter;
  const generator = new THREE.PMREMGenerator(renderer), shown = [new THREE.Color(-1, -1, -1), new THREE.Color(), new THREE.Color()], color = new THREE.Color();
  let target: THREE.WebGLRenderTarget | null = null;
  return {
    get texture() { return target?.texture ?? null; },
    update(sky: { hemiSky: THREE.Color; haze: THREE.Color; hemiGround: THREE.Color }) {
      const wanted = [sky.hemiSky, sky.haze, sky.hemiGround];
      if (target && wanted.every((value, i) => Math.abs(value.r - shown[i].r) + Math.abs(value.g - shown[i].g) + Math.abs(value.b - shown[i].b) < .02)) return;
      wanted.forEach((value, i) => shown[i].copy(value));
      for (let y = 0; y < height; y++) {
        // Row 0 is straight down; the horizon sits at the middle row.
        const up = y / (height - 1) * 2 - 1;
        if (up < 0) color.copy(sky.haze).lerp(sky.hemiGround, Math.min(1, -up * 4));
        else color.copy(sky.haze).lerp(sky.hemiSky, Math.min(1, Math.sqrt(up * 1.6)));
        const rgb = color.clone().convertLinearToSRGB();
        for (let x = 0; x < width; x++) data.set([rgb.r * 255, rgb.g * 255, rgb.b * 255, 255], (y * width + x) * 4);
      }
      strip.needsUpdate = true;
      target = generator.fromEquirectangular(strip, target);
    },
    dispose() { target?.dispose(); generator.dispose(); strip.dispose(); },
  };
}
/** Farthest ground distance from the camera's focus that any screen corner can show (the view is a stretched rectangle on the ground, so corners reach farthest). */
function visibleRadius(camera: THREE.OrthographicCamera) {
  const direction = camera.getWorldDirection(look), halfWidth = (camera.right - camera.left) / 2, halfHeight = (camera.top - camera.bottom) / 2;
  return 1.05 * Math.hypot(halfWidth, halfHeight / Math.max(.2, -direction.y)) / camera.zoom;
}

/**
 * The coast's lighting follows the server's in-game clock: a copper sun low in the morning and evening, a high bright
 * sun at noon, and a cold moon at night, with haze, fog, sky bounce and exposure moving with it. Shadows are
 * camera-relative so they keep their resolution across the kilometer-wide map, and the low sun throws them long.
 */
export function createSunsetLighting(scene: THREE.Scene, renderer: THREE.WebGLRenderer) {
  const sky = createSky(); sampleSky(CLOCK_START_MINUTES, sky);
  renderer.setClearColor(sky.haze); renderer.toneMapping = THREE.ACESFilmicToneMapping;
  const fog = new THREE.Fog(sky.haze, 110, 245); scene.fog = fog;
  const hemisphere = new THREE.HemisphereLight(sky.hemiSky, sky.hemiGround, sky.hemiIntensity); scene.add(hemisphere);
  const sun = new THREE.DirectionalLight(sky.key, sky.keyIntensity); sun.castShadow = true;
  sun.shadow.mapSize.set(2048, 2048); sun.shadow.normalBias = .05; sun.shadow.bias = -.0002;
  sun.shadow.camera.near = .5; sun.shadow.camera.far = 340;
  scene.add(sun, sun.target);
  // Shadow side catches the violet sky instead of going muddy brown.
  const fill = new THREE.DirectionalLight(0x7a92e0, 1.1); fill.position.set(60, 55, -50); scene.add(fill);
  // A thin rim of sea light from the south-west edges the western faces of rock and walls.
  const rim = new THREE.DirectionalLight(0xff7e6b, .55); rim.position.set(-70, 14, -90); scene.add(rim);
  const reflection = createSkyReflection(renderer);
  // In place before the loading gate warms shaders: a material compiled without the environment needs a second program once
  // it arrives, and a short-lived effect (smoke, spells) would compile that program again every time it is cast.
  reflection.update({ hemiSky: hemisphere.color, haze: fog.color, hemiGround: hemisphere.groundColor });
  scene.environment = reflection.texture; scene.environmentIntensity = TUNING.reflections * Math.min(1, hemisphere.intensity / 2.3);
  const focus = new THREE.Vector3(), direction = new THREE.Vector3();
  return {
    /** The lights and fog, for the weather to dim and thicken after each update. */
    lights: { hemisphere, sun, fill, rim }, fog,
    /** `clock` is the server's clock in minutes since the first midnight. */
    /**
     * Call last, after the weather. Fog is measured from the camera, which never moves when the view zooms, so the same
     * distances would fog the middle of the screen differently at every zoom. This stretches the fog about the point the
     * camera looks at by 1/zoom, so what a viewer sees (fog at the party, how fast it thickens toward the top of the
     * screen) is the same at any zoom, and identical to the zoom-1 look.
     */
    fit(camera: THREE.OrthographicCamera) {
      // The reflected sky is the one the weather left: grey under rain, the fog's colour at the horizon.
      reflection.update({ hemiSky: hemisphere.color, haze: fog.color, hemiGround: hemisphere.groundColor });
      scene.environment = reflection.texture; scene.environmentIntensity = TUNING.reflections * Math.min(1, hemisphere.intensity / 2.3);
      camera.getWorldDirection(direction);
      const focusDepth = camera.position.y / Math.max(.2, -direction.y);
      fog.near = focusDepth + (fog.near - focusDepth) / camera.zoom; fog.far = focusDepth + (fog.far - focusDepth) / camera.zoom;
    },
    update(camera: THREE.OrthographicCamera, clock: number) {
      sampleSky(clock, sky);
      renderer.setClearColor(sky.haze); renderer.toneMappingExposure = sky.exposure * TUNING.exposure; fog.color.copy(sky.haze);
      hemisphere.color.copy(sky.hemiSky); hemisphere.groundColor.copy(sky.hemiGround); hemisphere.intensity = sky.hemiIntensity * TUNING.ambient;
      sun.color.copy(sky.key); sun.intensity = sky.keyIntensity * TUNING.sun; fill.intensity = sky.fillIntensity * TUNING.fill; rim.intensity = sky.rimIntensity * TUNING.rim;
      sun.shadow.radius = TUNING.shadowSoftness; sun.shadow.normalBias = TUNING.shadowNormalBias;
      camera.getWorldDirection(direction);
      focus.copy(camera.position).addScaledVector(direction, -camera.position.y / direction.y);
      const reach = Math.min(MAX_REACH, Math.max(MIN_REACH, visibleRadius(camera)));
      sun.target.position.copy(focus); sun.position.copy(focus).add(sky.keyOffset);
      Object.assign(sun.shadow.camera, { left: -reach, right: reach, top: reach, bottom: -reach }); sun.shadow.camera.updateProjectionMatrix();
      // The distances at zoom 1; the weather thickens them, then `fit` stretches them to the zoom.
      fog.near = 95 * TUNING.fogDistance; fog.far = 245 * TUNING.fogDistance;
    },
    /** The sky reflection's GPU targets, which outlive a disposed scene on a renderer that is kept. */
    dispose() { reflection.dispose(); },
  };
}
